import { useState, Fragment } from "react";
import { Combobox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  ArrowLongLeftIcon,
  MinusSmallIcon,
} from "@heroicons/react/20/solid";
import fontSizes from "../../../assets/font_sizes.json";
import fonts from "../../../assets/font_families.json";

export default function NameStyleSettings({ setFormData, formData }) {
  const handleColorChange = (e) => {
    const { value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      nameAttribute: {
        ...prevState.nameAttribute,
        textColor: value,
      },
    }));
  };
  const handleClick = (e, { name, value = "", toggle }) => {
    e.preventDefault();
    const alternateVal = {
      fontWeight: "normal",
      fontStyle: "normal",
      textTransform: "capitalize",
      textAlign: "center",
    };

    if (toggle) {
      setFormData((prevState) => ({
        ...prevState,
        nameAttribute: {
          ...prevState.nameAttribute,
          [name]:
            formData.nameAttribute[name] === value ? alternateVal[name] : value,
        },
      }));
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      nameAttribute: {
        ...prevState.nameAttribute,
        [name]: value,
      },
    }));
  };

  const handleSelectChange = (selected, name) => {
    setFormData((prevState) => ({
      ...prevState,
      nameAttribute: {
        ...prevState.nameAttribute,
        [name]: selected,
      },
    }));
  };

  return (
    <div className="flex flex-wrap gap-4">
      <Select
        list={fonts}
        onChange={handleSelectChange}
        value={formData.nameAttribute.fontFamily}
        name="fontFamily"
      />
      <div className="w-20">
        <Select
          list={fontSizes}
          onChange={handleSelectChange}
          value={formData.nameAttribute.fontSize}
          name="fontSize"
        />
      </div>
      <div className="flex">
        <input
          name="textColor"
          onChange={handleColorChange}
          type="color"
          className="w-10 self-center"
          defaultValue="#000000"
        ></input>
      </div>
      <button
        className={`border border-greyBorder rounded-lg px-3 ${
          formData.nameAttribute.fontWeight === "bold" ? "bg-greyBg" : ""
        }`}
        onClick={(e) =>
          handleClick(e, { name: "fontWeight", value: "bold", toggle: true })
        }
      >
        B
      </button>
      <button
        onClick={(e) =>
          handleClick(e, { name: "fontStyle", value: "italic", toggle: true })
        }
        className={`border border-greyBorder rounded-lg px-3 ${
          formData.nameAttribute.fontStyle === "italic" ? "bg-greyBg" : ""
        }`}
      >
        I
      </button>
      <button
        onClick={(e) =>
          handleClick(e, {
            name: "textTransform",
            value: "uppercase",
            toggle: true,
          })
        }
        className={`border border-greyBorder rounded-lg px-3 ${
          formData.nameAttribute.textTransform === "uppercase"
            ? "bg-greyBg"
            : ""
        }`}
      >
        A
      </button>
      <button
        type="button"
        onClick={(e) =>
          handleClick(e, { name: "textAlign", value: "lpad", toggle: true })
        }
        className={`relative border border-greyBorder rounded-lg px-3 ${
          formData.nameAttribute.textAlign === "lpad"
            ? "bg-greyBg"
            : "bg-transparent"
        }`}
      >
        <>
          <MinusSmallIcon className="w-5 absolute right-5 rotate-90" />
          <ArrowLongLeftIcon className="w-5 -z-1" />
        </>
      </button>
      <button
        onClick={(e) =>
          handleClick(e, { name: "textAlign", value: "rpad", toggle: true })
        }
        className={`relative rotate-180 border border-greyBorder rounded-lg px-3 ${
          formData.nameAttribute.textAlign === "rpad"
            ? "bg-greyBg"
            : "bg-transparent"
        }`}
      >
        <MinusSmallIcon className="w-5 absolute right-5 rotate-90" />
        <ArrowLongLeftIcon className="w-5 " />
      </button>
    </div>
  );
}

function Select({ list, onChange, value, name }) {
  const [query, setQuery] = useState("");

  const filteredList =
    query === ""
      ? list
      : list.filter((item) =>
          item.name
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  return (
    <Combobox value={value} onChange={(selected) => onChange(selected, name)}>
      <div className="relative mt-1">
        <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left sm:text-sm border border-greyBorder">
          <Combobox.Input
            className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
            displayValue={(person) => person.name}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredList.length === 0 && query !== "" ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Nothing found.
              </div>
            ) : (
              filteredList.map((item) => (
                <Combobox.Option
                  key={item.value}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active
                        ? "bg-artfulRed-100 text-artfulRed"
                        : "text-gray-900"
                    }`
                  }
                  value={item}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {item.name}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? "text-white" : "text-teal-600"
                          }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
